// src/features/developers/useDeveloperDocs.ts
//
// Loads what the page is fed from: the information of the section and the OpenAPI document. The
// document is kept both parsed (for the reference and the event sample) and as text (for the
// download, byte for byte as served).
import { useEffect, useState } from 'react';
import { API_CONFIG } from '@/config/constants';
import { developersApi } from './api';
import { stripTrailingSlash } from './components/common';
import type { DeveloperInfo } from './types';

export type DocsFailure = 'forbidden' | 'unavailable' | null;

export interface DeveloperDocs {
  info: DeveloperInfo | null;
  spec: unknown;
  specText: string;
  failure: DocsFailure;
  /** The address of the environment this application runs against, for samples and downloads. */
  baseUrl: string;
}

/** The address the application's own API lives on, without the UI path: the fallback while `info` loads. */
export const fallbackBaseUrl = (apiUrl: string | undefined): string => {
  try {
    return apiUrl ? new URL(apiUrl).origin : '';
  } catch {
    return '';
  }
};

export const baseUrlOf = (info: DeveloperInfo | null, apiUrl: string | undefined): string => {
  const current = info?.environments.find((e) => e.key === info.currentEnvironment) ?? info?.environments[0];
  return stripTrailingSlash(current?.apiBaseUrl ?? fallbackBaseUrl(apiUrl));
};

export const useDeveloperDocs = (): DeveloperDocs => {
  const [info, setInfo] = useState<DeveloperInfo | null>(null);
  const [spec, setSpec] = useState<unknown>(null);
  const [specText, setSpecText] = useState('');
  const [failure, setFailure] = useState<DocsFailure>(null);

  useEffect(() => {
    let alive = true;
    // Independent: the information alone is enough for the first tab; a missing document only
    // costs the reference, the event sample and the OpenAPI download.
    developersApi
      .info()
      .then((value) => alive && setInfo(value))
      .catch((error: { status?: number }) => alive && setFailure(error.status === 403 ? 'forbidden' : 'unavailable'));
    developersApi
      .openApiText()
      .then((text) => {
        if (!alive) return;
        setSpecText(text);
        setSpec(JSON.parse(text));
      })
      .catch((error: { status?: number }) => alive && setFailure((current) => current ?? (error.status === 403 ? 'forbidden' : 'unavailable')));
    return () => {
      alive = false;
    };
  }, []);

  return { info, spec, specText, failure, baseUrl: baseUrlOf(info, API_CONFIG.BASE_URL) };
};
